<script setup lang="js">

import AuthedLayout from "@/layouts/AuthedLayout.vue";
import { computed, onMounted, ref } from 'vue'
import { useAuthStore } from "@/stores/authStore.js";
import TextEntryModal from '@/components/Alerts/TextEntryModal.vue'
import { dataSubmissionApi } from '@/api/DataSubmission/DataSubmissionApi.js'
import { ButtonSize, ButtonWidth } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import { Banner, Toaster } from '@/models/utils/Toaster.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import Popper from 'vue3-popper'
import "@comagine/vue3-datatable/dist/style.css"
import { Format } from '@/models/utils/FormatUtils.js'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'
import { Tenants } from '@/models/tenants/Tenants.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import EndpointTableCellMenu from '@/components/Tables/EndpointTableCellMenu.vue'
import DataSubmissionHistoryList from '@/views/Data/DataSubmissionHistoryList.vue'

const authStore = useAuthStore();

const dataSubmissionToEdit = ref(null)
const dataSubmissionToDelete = ref(null)
const dataSubmissionToView = ref(null)
const editDescriptionTitle = ref("Edit Description")
const editDescriptionMessage = ref("")
const isDescriptionSaving = ref(false)
const isDeleting = ref(false)
const dataSubmissionToEditDescription = computed(() => {
	return dataSubmissionToEdit.value?.description ?? ''
})
const showDataSubmissionEditDialog = computed(() => {
	return dataSubmissionToEdit.value != null
})
const toaster = new Toaster()
const banner = new Banner()
const table = ref(null)
const includeArchivedAndDeleted = ref(false)
const archiveAfterDays = ref(0)
const showUserNameColumn = authStore.isAnyAdmin || authStore.canViewAllOrgFiles
const isDownloading = ref(false)
const selectedDataSubmissions = ref([])

const columns = [
	{ field: "id", title: "", isUnique: true, hide: true },
	{ field: "file_name", title: "File Name", cellClass: 'min-w-80 overflow-auto break-all' },
	{ field: "updated_at", title: "Date Submitted", width: '1%' },
	{ field: "status", title: "Status", width: '1%' },
	{ field: "description", title: "Description", cellClass: 'min-w-80 overflow-auto' },
	{ field: "user_name", title: "Submitter", hide: !showUserNameColumn, width: '1%' },
	{ field: "actions", title: "Actions", sort: false, width: '1%' }
]

const endpointParams = {
	"organization_id": authStore.userOrganizationData.activeOrganizationId
}

const advancedSearchParams = computed(() => {
	return [
		{
			"id": 'delete',
			"value": includeArchivedAndDeleted.value
		},
		{
			"id": 'archive',
			"value": includeArchivedAndDeleted.value
		}
	]
})

const endpoints = [
	new DataTableEndpointDefinition({
		endpointRoute: 'data-submission/all',
		endpointParams: endpointParams,
		advancedSearchParams: advancedSearchParams,
		initialSortColumn: 'updated_at',
		initialSortDirection: 'desc',
		searchPlaceholder: 'Search by file name, user name, or description',
		showCheckboxes: authStore.isPortalAdmin || authStore.canDownloadData,
		columns: columns,
	})
]

onMounted(() => {
	archiveAfterDays.value = Number(JSON.parse(authStore.getAuth.tenantInfo().settings_json)?.data_submissions_archive_after_days?.value)
})

function rowSelected(rows) {
	selectedDataSubmissions.value = rows
}

function selectAllToggled(rows) {
	selectedDataSubmissions.value = rows
}

function editDescription(p_data)
{
	editDescriptionTitle.value = p_data.description && p_data.description.length > 0 ? "Edit Description" : "Add a Description";
	editDescriptionMessage.value = "For <b>" + p_data.file_name + "</b>";
	dataSubmissionToEdit.value = { 'id': p_data.id, 'description': p_data.description }
}

function saveDescription(description) {
	isDescriptionSaving.value = true

	dataSubmissionApi.updateDescription(
		dataSubmissionToEdit.value.id,
		description,
		() => {
			toaster.success('Description updated successfully!')

			isDescriptionSaving.value = false
			dataSubmissionToEdit.value = null

			table.value.loadData()
		}, () => {
			isDescriptionSaving.value = false
			dataSubmissionToEdit.value = null

			toaster.error('There was an error updating the description, please try again.')
		}
	)
}

function toggleArchivedContent() {
	table.value.loadData()
}

function onDownloadClicked(filesToDownload)
{
	isDownloading.value = true

	toaster.info('Requesting download...', { timeout: false })

	dataSubmissionApi.download(
		filesToDownload,
		authStore.getAuth.tenantInfo().name,
		authStore.getActiveOrganization.organization_name,
		() => {
			isDownloading.value = false

			toaster.clear()
			toaster.success((filesToDownload.length > 1 ? 'Data submissions' : 'Data submission') + ' downloaded successfully.')
		},
		(p_err) => {
			isDownloading.value = false
			handleDownloadError(p_err, filesToDownload)
		}
	)
}

function deleteDataSubmission(reasonForDeletion)
{
	isDeleting.value = true

	toaster.info('Deleting data submissions...', { timeout: false })

	dataSubmissionApi.delete(
		dataSubmissionToDelete.value.id,
		reasonForDeletion,
		() => {
			isDeleting.value = false
			dataSubmissionToDelete.value = null

			toaster.clear()
			toaster.success('Data submission deleted successfully.')

			table.value.loadData()
		},
		() => {
			isDeleting.value = false
			dataSubmissionToDelete.value = null
			toaster.error('Data submission deletion failed.');
		}
	)
}

function canSelectRow(p_row)
{
	return !p_row['deleted_at']
}

async function handleDownloadError(p_err, filteredDataSubmissions)
{
	try
	{
		const devEnvEh = Tenants.isDevHost() && authStore.isPortalAdmin;
		const text = await p_err.response.data.text();
		const data = JSON.parse(text);

		toaster.clear()

		if (data['errors'] && data['errors'].length > 0)
		{
			const err_msgs = data['errors'].map(p_item => p_item.detail);
			if (devEnvEh)
			{
				banner.error("Dev Only Banner:<br>" + err_msgs.join('<br/>'));
			}

			const err = data['errors'].find((p_item) => p_item.status === 403);
			if (err)
			{
				toaster.error('You do not have permission to download data submissions.');
			}
			else
			{
				toaster.error((filteredDataSubmissions.length > 1 ? 'Data submissions' : 'Data submission') + ' failed to download.')
			}
		}
		else
		{
			toaster.error((filteredDataSubmissions.length > 1 ? 'Data submissions' : 'Data submission') + ' failed to download.')
		}
	}
	catch(e)
	{
		console.error(e)
	}
}

function getMenuItems(p_record)
{
	let items = [];

	const can_delete = p_record.deleted_at === null && authStore.canDeleteData
	const can_download = p_record.deleted_at === null && authStore.canDownloadData

	items.push({ id: 1, text:'History', hidden: p_record.history.length <= 1 })
	items.push({ id: 2, text:'Delete', hidden: !can_delete })
	items.push({ id: 3, text:'Download', hidden: !can_download })

	return items;
}

function onMenuItemSelected(p_menu_item, p_record)
{
	switch (p_menu_item.text) {
		case "Delete":
			dataSubmissionToDelete.value = p_record
			break;
		case "Download":
			onDownloadClicked([ p_record ])
			break;
		case "History":
			dataSubmissionToView.value = p_record
			break;
		default:
			alert("Not yet implemented")
			break;
	}
}

function getStatusStyle(p_status)
{
	let style = 'bg-neutral-light text-label';
	switch (p_status)
	{
		case 'Submitted':
			style = 'bg-success text-success-on-color';
			break;

		case 'Archived':
			style = 'bg-information text-information-on-color';
			break;

		case 'Deleted':
			style = 'bg-error-light text-error-on-color';
			break;
	}
	return style;
}

function getStatusText(record) {
	const archivedAtDate = new Date()
	archivedAtDate.setDate(archivedAtDate.getDate() - archiveAfterDays.value)

	if (record.deleted_at) {
		return 'Deleted'
	}

	if ((new Date(record.updated_at)) <= archivedAtDate) {
		return 'Archived'
	}

	else return 'Submitted'
}
</script>

<template>
	<AuthedLayout>
		<p class="dashboard-title">Submitted Data</p>

		<div class="p-8 rounded-xl bg-neutral border border-neutral-subtle card-shadow">
			<div class="flex flex-row mb-4">
				<p class="admin-center-subtitle">{{ (authStore.isAnyAdmin || authStore.canViewAllOrgFiles) ? "Data Submissions" : "My Submissions" }}</p>
				<SubmitButton v-if="authStore.isPortalAdmin || authStore.canDownloadData" label="Download" @click="onDownloadClicked(selectedDataSubmissions)" size="small" :disabled="selectedDataSubmissions.length === 0 || isDownloading" :loading="isDownloading"/>
			</div>
			<PortalDataTable
				ref="table"
				:endpoints="endpoints"
				:enableRowSelectFn="canSelectRow"
				@row-selected="rowSelected"
				@select-all="selectAllToggled"
			>
				<template #advanced-search>
					<div class="flex flex-row mr-1 mt-1 justify-end">
						<div class="flex w-full justify-end overflow-hidden gap-4">
							<Popper :content="`Data Submissions are considered to be archived after ${archiveAfterDays} days.`" hover arrow placement="bottom-start">
								<CheckBox input-id="cbx_archive" label="Include archived and deleted content" label-location="left" class="my-2" :checked="false" v-model="includeArchivedAndDeleted" @checked-changed="toggleArchivedContent" />
							</Popper>
						</div>
					</div>
				</template>

				<template #file_name="data">
					<div class="flex space-x-1">
						<Popper v-if="['initiated', 'queued', 's3_in_transit'].includes(data.value.status)" content="This data submission is still in the process of uploading and will be available soon." hover arrow placement="bottom-end">
							<img class="size-5 min-w-5 min-h-5" src="@/assets/images/icon/file-upload.svg"/>
						</Popper>

						<Popper v-if="data.value.status === 'error'" content="This data submission has failed to upload and will need to be deleted and uploaded again." hover arrow placement="bottom-end">
							<img class="size-5 min-w-5 min-h-5" src="@/assets/images/icon/file-exclamation.svg"/>
						</Popper>

						<span class="userpilot-mask">{{ data.value.file_name }}</span>
					</div>
				</template>

				<template #updated_at="data">
					<div class="whitespace-nowrap">
						{{ Format.date(data.value.updated_at) }}
					</div>
				</template>

				<template #description="data">
					<div class="flex h-full items-center justify-end cursor-pointer " @click="editDescription(data.value)">
						<div v-if="!data.value.description || data.value.description.length === 0" class="text-placeholder">Add file description</div>
						<span class="userpilot-mask grow mr-2" :title="data.value.description">{{ data.value.description }}</span>
						<svg class="flex-shrink-0 fill-icon-primary-dark hover:fill-icon-primary-hover hover:cursor-pointer" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
							<title>Edit description</title>
							<path fill-rule="evenodd" clip-rule="evenodd" d="M1.56622 3.23223C2.03506 2.76339 2.67094 2.5 3.33398 2.5H9.16732C9.62755 2.5 10.0006 2.8731 10.0006 3.33333C10.0006 3.79357 9.62755 4.16667 9.16732 4.16667H3.33398C3.11297 4.16667 2.90101 4.25446 2.74473 4.41074C2.58845 4.56702 2.50065 4.77899 2.50065 5V16.6667C2.50065 16.8877 2.58845 17.0996 2.74473 17.2559C2.90101 17.4122 3.11297 17.5 3.33398 17.5H15.0006C15.2217 17.5 15.4336 17.4122 15.5899 17.2559C15.7462 17.0996 15.834 16.8877 15.834 16.6667V10.8333C15.834 10.3731 16.2071 10 16.6673 10C17.1276 10 17.5006 10.3731 17.5006 10.8333V16.6667C17.5006 17.3297 17.2373 17.9656 16.7684 18.4344C16.2996 18.9033 15.6637 19.1667 15.0006 19.1667H3.33398C2.67094 19.1667 2.03506 18.9033 1.56622 18.4344C1.09738 17.9656 0.833984 17.3297 0.833984 16.6667V5C0.833984 4.33696 1.09738 3.70107 1.56622 3.23223Z"/>
							<path fill-rule="evenodd" clip-rule="evenodd" d="M16.6673 2.39909C16.4195 2.39909 16.1818 2.49754 16.0066 2.67278L8.25314 10.4262L7.81264 12.1882L9.57463 11.7477L17.3281 3.99427C17.5033 3.81903 17.6018 3.58135 17.6018 3.33352C17.6018 3.0857 17.5033 2.84802 17.3281 2.67278C17.1528 2.49754 16.9152 2.39909 16.6673 2.39909ZM14.8281 1.49427C15.3159 1.00647 15.9775 0.732422 16.6673 0.732422C17.3572 0.732422 18.0188 1.00647 18.5066 1.49427C18.9944 1.98207 19.2684 2.64367 19.2684 3.33352C19.2684 4.02338 18.9944 4.68498 18.5066 5.17278L10.5899 13.0894C10.4831 13.1962 10.3493 13.272 10.2028 13.3086L6.86945 14.142C6.58547 14.213 6.28506 14.1298 6.07808 13.9228C5.8711 13.7158 5.78789 13.4154 5.85888 13.1314L6.69222 9.79808C6.72885 9.65155 6.80461 9.51773 6.91141 9.41093L14.8281 1.49427Z"/>
						</svg>
					</div>
				</template>

				<template #status="data">
					<div class="flex w-full justify-center whitespace-nowrap">
						<div class="flex justify-center items-center w-22 py-1 px-2 rounded-md"
						     :class="getStatusStyle(getStatusText(data.value))">
							{{ getStatusText(data.value) }}
						</div>
					</div>
				</template>

				<template #user_name="data">
					<div class="userpilot-mask">
						{{ data.value.user_name }}
					</div>
				</template>

				<template #actions="data">
					<div v-if="getMenuItems(data.value).some(item => !item.hidden)" class="flex w-full justify-center">
						<EndpointTableCellMenu
							:items="getMenuItems(data.value)"
							:record="data.value"
							:disabled="false"
							@onMenuItemSelected="onMenuItemSelected"
						/>
					</div>
				</template>
			</PortalDataTable>
		</div>

		<Teleport to="html">
			<TextEntryModal
				v-if="showDataSubmissionEditDialog"
				:show="true"
				:title="editDescriptionTitle"
				:twoButtonModal="true"
				:button-size="ButtonSize.Small"
				:button-width="ButtonWidth.Full"
				button-text="Save"
				:default-text="dataSubmissionToEditDescription"
				:loading="isDescriptionSaving"
				@onButtonClicked="saveDescription"
				@dismiss="dataSubmissionToEdit = null"
				:max-length="100"
				placeholder="E.g., Corrected errors, added missing records, or updated formatting."
				:message="editDescriptionMessage"
				sub-input-message="Do not include PHI or PII in the description."
			/>
		</Teleport>

		<Teleport to="html">
			<TextEntryModal
				v-if="dataSubmissionToDelete != null"
				:show="true"
				title="Delete Submission"
				:twoButtonModal="true"
				:button-size="ButtonSize.Small"
				:button-width="ButtonWidth.Full"
				button-text="Delete"
				:loading="isDeleting"
				@onButtonClicked="deleteDataSubmission"
				@dismiss="dataSubmissionToDelete = null"
				:max-length="100"
				message="Please enter the reason for deletion."
				sub-input-message="Do not include PHI or PII in the reason."
			/>
		</Teleport>

		<Teleport to="html">
			<DataSubmissionHistoryList :show="true" v-if="dataSubmissionToView != null" :modal-data="dataSubmissionToView" @evCloseModal="dataSubmissionToView = null"/>
		</Teleport>
	</AuthedLayout>
</template>